/**
 * SoftwareViewer — Cloud OS Software Streaming Component
 *
 * Displays a running software session in the browser via iframe.
 * KasmWeb images provide their own web UI at port 6901 (KasmVNC) —
 * we simply iframe the HTTPS URL returned by the backend.
 */

import { HugeiconsIcon } from '@hugeicons/react';
import { Cancel01Icon as X, Maximize01Icon as Maximize2, Minimize01Icon as Minimize2, RefreshIcon as RefreshCw, SquareIcon as Square } from '@hugeicons/core-free-icons';
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { useAuthFetch } from "@/hooks/useAuthFetch";
import { toast } from "sonner";

interface SoftwareViewerProps {
  sessionId: string;
  streamUrl: string;
  softwareName: string;
  onClose: () => void;
}

export function SoftwareViewer({ sessionId, streamUrl, softwareName, onClose }: SoftwareViewerProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const authFetch = useAuthFetch();

  // Poll for task readiness (public IP assignment takes ~30s)
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);

  useEffect(() => {
    // For code-server, the streamUrl will be an HTTP URL once the task has a public IP
    // For now, use the streamUrl directly if it starts with http/ws
    if (streamUrl.startsWith("http") || streamUrl.startsWith("ws")) {
      setResolvedUrl(streamUrl);
      setIsLoading(false);
    } else {
      // Poll the backend for the actual URL
      const pollInterval = setInterval(async () => {
        try {
          const res = await authFetch(`/api/software/sessions`);
          if (res.ok) {
            const data = await res.json();
            const session = data.sessions?.find((s: any) => s.id === sessionId);
            if (session?.stream_url) {
              setResolvedUrl(session.stream_url);
              setIsLoading(false);
              clearInterval(pollInterval);
            }
          }
        } catch {
          // Keep polling
        }
      }, 3000);

      // Timeout after 60s
      const timeout = setTimeout(() => {
        clearInterval(pollInterval);
        if (!resolvedUrl) {
          setError("Session took too long to start. Please try again.");
          setIsLoading(false);
        }
      }, 60000);

      return () => {
        clearInterval(pollInterval);
        clearTimeout(timeout);
      };
    }
  }, [streamUrl, sessionId, authFetch]);

  const handleStop = async () => {
    try {
      await authFetch("/api/software/stop", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId }),
      });
      toast.success(`${softwareName} stopped`);
      onClose();
    } catch {
      toast.error("Failed to stop session");
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[9999] bg-black flex flex-col"
    >
      {/* Toolbar */}
      <div className="h-10 bg-neutral-900 border-b border-white/10 flex items-center justify-between px-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-white/80">{softwareName}</span>
          {isLoading && (
            <span className="text-[10px] text-yellow-400 animate-pulse">Starting...</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-white/60 hover:text-white"
            onClick={() => iframeRef.current?.contentWindow?.location.reload()}
            title="Refresh"
          >
            <HugeiconsIcon icon={RefreshCw} className="h-3.5 w-3.5"  />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-white/60 hover:text-white"
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <HugeiconsIcon icon={Minimize2} className="h-3.5 w-3.5"  /> : <HugeiconsIcon icon={Maximize2} className="h-3.5 w-3.5"  />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-red-400 hover:text-red-300 hover:bg-red-500/10"
            onClick={handleStop}
            title="Stop session"
          >
            <HugeiconsIcon icon={Square} className="h-3.5 w-3.5"  />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-white/60 hover:text-white"
            onClick={onClose}
            title="Close (keeps running)"
          >
            <HugeiconsIcon icon={X} className="h-3.5 w-3.5"  />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 relative">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="text-center space-y-4">
              <div className="h-12 w-12 mx-auto border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-white/70">Starting {softwareName}...</p>
              <p className="text-xs text-white/40">This may take 20-30 seconds</p>
            </div>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="text-center space-y-4 max-w-sm">
              <p className="text-sm text-red-400">{error}</p>
              <Button variant="outline" size="sm" onClick={onClose}>
                Go Back
              </Button>
            </div>
          </div>
        )}

        {resolvedUrl && !error && (
          <iframe
            ref={iframeRef}
            src={resolvedUrl}
            className="w-full h-full border-0"
            allow="clipboard-read; clipboard-write; fullscreen"
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals"
            onLoad={() => setIsLoading(false)}
          />
        )}
      </div>
    </div>
  );
}
